import { Forms } from "@/types/form";
import { log } from "@/utils/logger";
import { isDefined, isString } from "@/utils/validations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Params, useNavigate, useParams } from "react-router-dom";
import api from "@/config/api";
import { StepFormValues } from ".";

const clearUnusedProperties = (data: StepFormValues) => {
    if (!isString(data.description)) delete data.description;
    delete data.selectedBrandId;
    delete data.selectedOrganizationId;
    delete data.selectedProductIds;

    return data;
};

const useSubmit = (id: string) => {
    const { code } = useParams<Params>();
    const navigate = useNavigate();

    const queryClient = useQueryClient();
    const isUpdate = isDefined(code);

    return useMutation({
        mutationFn: async (campaign: StepFormValues) => {
            const data = clearUnusedProperties(campaign);
            const endpoint = isUpdate ? `/campaigns/${id}` : `/campaigns`;

            if (isUpdate) return await api.put(endpoint, { data });
            else return await api.post(endpoint, { data });
        },
        onMutate: async newCampaign => {
            await queryClient.cancelQueries({ queryKey: ["campaigns"] });
            const previousCampaigns = queryClient.getQueryData<Forms["Campaign"][]>(["campaigns"]);

            queryClient.setQueryData<Forms["Campaign"][]>(["campaigns"], campaigns => {
                if (isUpdate) {
                    return (
                        campaigns?.map(campaign =>
                            campaign.code === newCampaign.code
                                ? { ...campaign, ...newCampaign }
                                : campaign,
                        ) || []
                    );
                } else {
                    return campaigns ? [...campaigns, newCampaign] : [newCampaign];
                }
            });

            return { previousCampaigns };
        },
        onError: (error, _, context: { previousCampaigns?: Forms["Campaign"][] } | undefined) => {
            queryClient.setQueryData(["campaigns"], context?.previousCampaigns);
            log.error(error);
        },
        onSuccess: () => {
            log.success("Campaign has been successfully created");
            navigate(`/campaigns`);
            queryClient.invalidateQueries({ queryKey: ["campaigns"] });
        },
    });
};

export { useSubmit };
